import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/design-system/dropdown-menu';
import { FormModal } from '@/design-system/form-modal';
import { IconButton } from '@/design-system/icon-button';
import { Paginator } from '@/design-system/pagination';
import { SearchField } from '@/design-system/search-field';
import { Badge } from '@/design-system/badge';
import { TableData, TableDataColumn } from '@/design-system/table-data';
import { Tabs, TabsCount, TabsList, TabsTrigger } from '@/design-system/tabs';
import { toast } from '@/design-system/toaster';
import { SearchSelect } from '@/design-system/search-select';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';
import { Pitstop, getListPitstop } from '@/services/api/pitstops';
import {
  ForceTerminalAction,
  ImportCommitResult,
  SHIPMENT_SUMMARY_KEYS,
  Shipment,
  ShipmentExim,
  ShipmentListParams,
  ShipmentSummary,
  shipmentsAPI,
} from '@/services/api/shipments';
import {
  canForceTerminalShipment,
  type ShipmentStatus,
} from '@/services/api/status';
import { getListProvider, Provider } from '@/services/api/providers';
import {
  exportTemplatesAPI,
  ExportTemplateSummary,
} from '@/services/api/exportTemplates';

import {
  useUrlParam,
  useUrlNumber,
  useUrlPatch,
  useUrlSearch,
} from '@/hooks/urlState';

import AssignHubModal from './components/AssignHubModal';
import CancelShipmentModal from './components/CancelShipmentModal';
import CreateShipment4WModal from './components/CreateShipment4WModal';
import ForceTerminalModal from './components/ForceTerminalModal';
import ImportModal from './components/ImportModal';
import {
  ShipmentStatusBadge,
  ShipmentTypeTags,
} from './components/ShipmentBadges';
import ShipmentDetailPanel from './components/ShipmentDetailPanel';
import ShipmentFilterPanel, {
  EMPTY_FILTERS,
  DatePreset,
  ShipmentFilterValue,
  countActiveFilters,
  resolvePresetRange,
} from './components/ShipmentFilterPanel';
import { isShipmentLabelPrintable } from '@/components/LabelPreviewModal/shipmentLabels';
import { useShipmentLabels } from '@/components/LabelPreviewModal/useShipmentLabels';
import {
  canSetHub,
  displayAnchorRole,
  needsHubResolve,
} from './shipmentHandover';

// One tab per shipment status — all ten are filterable (07 §2.7). `AT_HUB`,
// `RETURNED` and `FAILED` are the v2 additions; `PARTIALLY_DISPATCHED` /
// `DISPATCHED` stay because a dispatch commit still rolls the AWB into them.
type TabKey = 'ALL' | ShipmentStatus;

const TABS: { key: TabKey; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'CREATED', label: 'Created' },
  { key: 'AT_HUB', label: 'At hub' },
  { key: 'PARTIALLY_DISPATCHED', label: 'Partially dispatched' },
  { key: 'DISPATCHED', label: 'Dispatched' },
  { key: 'IN_DELIVERY', label: 'In delivery' },
  { key: 'IN_DISPUTE', label: 'In dispute' },
  { key: 'COMPLETED', label: 'Completed' },
  { key: 'RETURNED', label: 'Returned' },
  { key: 'CANCELLED', label: 'Cancelled' },
  { key: 'FAILED', label: 'Failed' },
];

const DEFAULT_PAGE_SIZE = 20;

const TAB_KEYS = TABS.map((t) => t.key) as readonly TabKey[];
// Status-tab switch clears search + page, keeps the filter-popover filters.
const TAB_RESET_PARAMS = ['q'] as const;
const DATE_PRESET_KEYS: readonly DatePreset[] = [
  'today',
  'yesterday',
  'last_7_days',
  'last_30_days',
];

// URL param keys for the applied filter object (draft stays in local state).
const FILTER_PARAMS = {
  hubId: 'hubId',
  clientId: 'clientId',
  uploadShipmentCode: 'uploadCode',
  datePreset: 'preset',
  createdFrom: 'from',
  createdTo: 'to',
  exim: 'exim',
  driver: 'driver',
} as const;

const EXIM_KEYS: ShipmentExim[] = ['IMPORT', 'EXPORT', 'GENERAL'];

const ShipmentsPage: React.FC = () => {
  // Tab / search / pagination / filters are URL-driven (shareable, back-button
  // aware). Tab uses push history; everything else replace. Filter/tab/search
  // changes drop `page` in the same URL write (no double-fetch race).
  const [tab, setTab] = useUrlParam<TabKey>('tab', 'ALL', {
    allowed: TAB_KEYS,
    resetPage: true,
    resetParams: TAB_RESET_PARAMS,
    history: 'push',
  });

  // BE supports `?search=...` (ILIKE waybill | booking_id) — see
  // SHIPMENTS-API-REFERENCE §4. URL-backed; the box resyncs on external URL
  // changes (tab switch drops `q`, Back/forward). Debounced write.
  const {
    value: search,
    setValue: setSearch,
    committed: debouncedSearch,
  } = useUrlSearch('q', { resetPage: true, delay: 500 });

  // Applied filters are derived from the URL; the draft stays local so the
  // popover can be cancelled. Apply/reset write every field in one patch.
  const { searchParams, patch: patchUrl } = useUrlPatch();
  const filters: ShipmentFilterValue = useMemo(() => {
    const preset = searchParams.get(FILTER_PARAMS.datePreset);
    return {
      hubId: searchParams.get(FILTER_PARAMS.hubId) ?? '',
      clientId: searchParams.get(FILTER_PARAMS.clientId) ?? '',
      uploadShipmentCode:
        searchParams.get(FILTER_PARAMS.uploadShipmentCode) ?? '',
      datePreset: DATE_PRESET_KEYS.includes(preset as DatePreset)
        ? (preset as DatePreset)
        : '',
      createdFrom: searchParams.get(FILTER_PARAMS.createdFrom) ?? '',
      createdTo: searchParams.get(FILTER_PARAMS.createdTo) ?? '',
      exim: EXIM_KEYS.includes(
        searchParams.get(FILTER_PARAMS.exim) as ShipmentExim
      )
        ? (searchParams.get(FILTER_PARAMS.exim) as ShipmentExim)
        : '',
      driver: searchParams.get(FILTER_PARAMS.driver) ?? '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);
  const [filterDraft, setFilterDraft] =
    useState<ShipmentFilterValue>(EMPTY_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);

  const [items, setItems] = useState<Shipment[]>([]);
  // Per-status counts for the tab chips. `null` = not loaded / unavailable.
  const [summary, setSummary] = useState<ShipmentSummary | null>(null);
  const [tableLoading, setTableLoading] = useState(true);
  const [page, setPage] = useUrlNumber('page', 1, { min: 1 });
  const [pageSize, setPageSize] = useUrlNumber('pageSize', DEFAULT_PAGE_SIZE, {
    min: 1,
    resetPage: true,
  });
  const [total, setTotal] = useState(0);

  const [hubs, setHubs] = useState<Pitstop[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);

  const [importOpen, setImportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportMode, setExportMode] = useState<'default' | 'custom'>('default');
  const [exportClientId, setExportClientId] = useState('');
  // Direction tag for Custom export ('' = all directions).
  const [exportExim, setExportExim] = useState<ShipmentExim | ''>('');
  const [exportTemplateKey, setExportTemplateKey] = useState('');
  const [exportTemplates, setExportTemplates] = useState<
    ExportTemplateSummary[]
  >([]);

  // Server-side searched + paginated client picker for the Custom export — the
  // same hook the 4W create modal uses, so typing hits `/v1/providers?search=`
  // instead of filtering a one-shot 200-row page locally.
  const exportProviders = useProvidersDropdown(exportOpen);

  /**
   * The open detail panel, IN THE URL as `?detail=<shipmentId>`.
   *
   * Local state before, which made the one thing an operator most wants to
   * hand to someone else — "look at this shipment" — the one thing they could
   * not paste. It also meant Back closed nothing: the panel survived a history
   * step because history never knew it was open.
   *
   * `history: 'push'` so Back closes the panel instead of leaving the page,
   * matching how `tab` behaves. Empty string is the closed state, the same
   * sentinel `useUrlParam` uses for every other optional param here.
   */
  const [detailShipmentId, setDetailShipmentId] = useUrlParam<string>(
    'detail',
    '',
    { history: 'push' }
  );

  const [cancelTarget, setCancelTarget] = useState<Shipment | null>(null);

  // Force-terminal override (Mark completed / Mark failed) — moved off the
  // detail panel to the row kebab. One modal instance driven by the target.
  const [overrideTarget, setOverrideTarget] = useState<{
    shipment: Shipment;
    action: ForceTerminalAction;
  } | null>(null);

  // Shipping-label print — the same label the Packages page prints. The
  // selection is scoped to the current view (cleared when the query or page
  // changes) so the count never includes rows the user can't see.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Bulk "Set hub": the selected shipments that still need one.
  const [assignHubFor, setAssignHubFor] = useState<Shipment[]>([]);

  const searchInputRef = useRef<HTMLInputElement>(null);

  // Hub list for the filter dropdown + a provider page used only as the
  // `providerLookup` fallback for the Client column (rows saved before the BE
  // snapshotted `client`). The filter panel searches providers server-side on
  // its own, so it no longer reads this list.
  useEffect(() => {
    let cancelled = false;
    getListPitstop({ type: 'HUB', hasStaff: true, page: '1', size: '100' })
      .then((res) => {
        if (!cancelled) setHubs(res.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setHubs([]);
      });
    getListProvider({ page: '1', size: '200' })
      .then((res) => {
        if (!cancelled) setProviders(res.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setProviders([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Every request on this page (list, summary, export) narrows by the same
  // filters — built once so the tab counts can never disagree with the table.
  // Status and paging are added by each caller.
  const filterParams = useMemo(() => {
    const params: Omit<ShipmentListParams, 'page' | 'limit' | 'status'> = {};
    if (debouncedSearch) params.search = debouncedSearch;
    if (filters.hubId) {
      const n = Number(filters.hubId);
      if (Number.isFinite(n) && n > 0) params.hubId = n;
    }
    if (filters.uploadShipmentCode)
      params.uploadShipmentCode = filters.uploadShipmentCode;
    if (filters.clientId) {
      const n = Number(filters.clientId);
      if (Number.isFinite(n) && n > 0) params.clientId = n;
    }
    if (filters.exim) params.exim = filters.exim;
    if (filters.driver.trim()) params.driver = filters.driver.trim();
    // Date: preset wins (its inputs are cleared when a preset is active);
    // otherwise fall back to manual range. BE interprets dates in the tz
    // already sent by axios via the `X-Client-Time-Zone` header.
    const presetRange = resolvePresetRange(filters.datePreset);
    if (presetRange) {
      params.createdFrom = presetRange.from;
      params.createdTo = presetRange.to;
    } else {
      if (filters.createdFrom) params.createdFrom = filters.createdFrom;
      if (filters.createdTo) params.createdTo = filters.createdTo;
    }
    return params;
  }, [debouncedSearch, filters]);

  const fetchList = useCallback(async () => {
    setTableLoading(true);
    const params: ShipmentListParams = {
      ...filterParams,
      page,
      limit: pageSize,
    };
    if (tab !== 'ALL') params.status = tab;

    try {
      const res = await shipmentsAPI.list(params);
      setItems(res.data ?? []);
      setTotal(res.pagination?.total ?? 0);
    } catch (err) {
      setItems([]);
      setTotal(0);
      toast.error('Could not load shipments', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setTableLoading(false);
    }
  }, [tab, page, pageSize, filterParams]);

  // Tab chips. Non-blocking: a failing summary leaves the chips off rather
  // than breaking the table.
  const fetchSummary = useCallback(async () => {
    try {
      setSummary(await shipmentsAPI.summary(filterParams));
    } catch {
      setSummary(null);
    }
  }, [filterParams]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Keyed on the serialized query: `filterParams` is rebuilt on every URL
  // change, including opening the detail panel, which must keep the selection.
  const selectionScope = JSON.stringify([tab, page, pageSize, filterParams]);
  useEffect(() => {
    setSelectedIds([]);
  }, [selectionScope]);

  // "/" focuses the search box (matches invoices page UX).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inEditable =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable;
      if (inEditable) return;
      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleRefresh = () => {
    fetchList();
  };

  const handleImported = (result: ImportCommitResult, clientName: string) => {
    // Toast already fired by the modal. Refetch the list so the new batch's
    // shipments show up immediately. Reuse `clientName` only for the
    // optional batch filter UX later — for now we just refresh.
    void clientName;
    void result;
    fetchList();
  };

  const runExport = async (overrides?: {
    clientId?: number;
    templateKey?: string;
    /** Custom export's chosen direction; '' clears the filter (all directions). */
    exim?: ShipmentExim | '';
  }) => {
    setExporting(true);
    try {
      const exportParams: Parameters<typeof shipmentsAPI.exportCSV>[0] = {};
      if (tab !== 'ALL') exportParams.status = tab;
      if (debouncedSearch) exportParams.search = debouncedSearch;
      if (filters.hubId) {
        const n = Number(filters.hubId);
        if (Number.isFinite(n) && n > 0) exportParams.hubId = n;
      }
      if (filters.uploadShipmentCode)
        exportParams.uploadShipmentCode = filters.uploadShipmentCode;
      if (filters.clientId) {
        const n = Number(filters.clientId);
        if (Number.isFinite(n) && n > 0) exportParams.clientId = n;
      }
      if (filters.exim) exportParams.exim = filters.exim;
      if (filters.driver.trim()) exportParams.driver = filters.driver.trim();
      const presetRange = resolvePresetRange(filters.datePreset);
      if (presetRange) {
        exportParams.createdFrom = presetRange.from;
        exportParams.createdTo = presetRange.to;
      } else {
        if (filters.createdFrom) exportParams.createdFrom = filters.createdFrom;
        if (filters.createdTo) exportParams.createdTo = filters.createdTo;
      }
      // Custom export overrides the client filter + picks the template explicitly,
      // and its Direction tag is authoritative — a chosen direction filters the
      // rows, and '' clears any inherited exim so all directions are exported.
      if (overrides?.clientId != null)
        exportParams.clientId = overrides.clientId;
      if (overrides?.templateKey)
        exportParams.templateKey = overrides.templateKey;
      if (overrides?.exim !== undefined) {
        if (overrides.exim) exportParams.exim = overrides.exim;
        else delete exportParams.exim;
      }

      await shipmentsAPI.exportCSV(exportParams);
      setExportOpen(false);
    } catch (err) {
      toast.error('Export failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setExporting(false);
    }
  };

  const openExport = () => {
    setExportMode('default');
    setExportClientId(filters.clientId ? String(filters.clientId) : '');
    setExportExim(filters.exim ?? '');
    setExportTemplateKey('');
    setExportOpen(true);
    exportTemplatesAPI
      .listTemplates()
      .then((t) => {
        setExportTemplates(t);
        setExportTemplateKey(t[0]?.key ?? '');
      })
      .catch(() => setExportTemplates([]));
  };

  const submitExport = () => {
    if (exportMode === 'default') return runExport();
    const cid = Number(exportClientId);
    if (!cid)
      return toast.error('Pick a client', {
        description: 'Custom export needs a client.',
      });
    if (!exportTemplateKey)
      return toast.error('Pick a template', {
        description: 'Custom export needs a template.',
      });
    return runExport({
      clientId: cid,
      templateKey: exportTemplateKey,
      exim: exportExim,
    });
  };

  const openCancel = (shipment: Shipment) => {
    setCancelTarget(shipment);
  };

  const openOverride = (shipment: Shipment, action: ForceTerminalAction) => {
    setOverrideTarget({ shipment, action });
  };

  const providerLookup = useMemo(() => {
    const map = new Map<number, string>();
    providers.forEach((p) => map.set(p.id, p.name));
    return map;
  }, [providers]);

  // Row kebab (one id) and the bulk "Print labels" button (the selection) —
  // the shared hook fetches the items, maps them and opens the label modal.
  // The provider lookup backs the label header for rows saved before the BE
  // snapshotted `client`.
  const labelFallbackClient = useCallback(
    (s: Shipment) => {
      const name = providerLookup.get(s.clientId);
      return name ? { id: s.clientId, name } : null;
    },
    [providerLookup]
  );
  const labels = useShipmentLabels({ fallbackClient: labelFallbackClient });

  // The selected rows on this page whose hub ops may set: unresolved (red
  // warning) or already AT_HUB (moved to another hub).
  const hubResolveSelection = useMemo(() => {
    const selected = new Set(selectedIds);
    return items.filter((s) => selected.has(s.id) && canSetHub(s));
  }, [items, selectedIds]);
  const openAssignHub = () => setAssignHubFor(hubResolveSelection);

  // ── Cell class registers (mirror invoices page) ──
  const codeCls =
    'font-mono text-[12px] font-semibold text-text-strong-950 tabular-nums';
  const dateCls = 'text-[13px] text-text-sub-600 tabular-nums';
  const nameCls = 'text-[13px] text-text-strong-950 tracking-tight';
  const metaCls = 'text-[11px] text-text-sub-600';

  // ── Filter popover handlers (mirror invoice) ──
  const activeFilterCount = useMemo(
    () => countActiveFilters(filters),
    [filters]
  );

  // Seed the draft from the applied filters whenever the popover opens.
  const handleFilterOpenChange = (open: boolean) => {
    if (open) setFilterDraft(filters);
    setFilterOpen(open);
  };

  // Write every filter field + drop `page` in ONE URL patch (atomic — one
  // fetch, no stale-page race).
  const writeFilters = (v: ShipmentFilterValue) => {
    patchUrl((params) => {
      const setOrDelete = (key: string, value: string) => {
        if (value) params.set(key, value);
        else params.delete(key);
      };
      setOrDelete(FILTER_PARAMS.hubId, v.hubId);
      setOrDelete(FILTER_PARAMS.clientId, v.clientId);
      setOrDelete(FILTER_PARAMS.uploadShipmentCode, v.uploadShipmentCode);
      setOrDelete(FILTER_PARAMS.datePreset, v.datePreset);
      setOrDelete(FILTER_PARAMS.createdFrom, v.createdFrom);
      setOrDelete(FILTER_PARAMS.createdTo, v.createdTo);
      setOrDelete(FILTER_PARAMS.exim, v.exim);
      setOrDelete(FILTER_PARAMS.driver, v.driver.trim());
      params.delete('page');
    });
  };

  const applyFilter = () => {
    writeFilters(filterDraft);
    setFilterOpen(false);
  };

  const resetFilter = () => {
    setFilterDraft(EMPTY_FILTERS);
    writeFilters(EMPTY_FILTERS);
    setFilterOpen(false);
  };

  const columns: TableDataColumn<Shipment>[] = [
    {
      key: 'waybill',
      title: 'Waybill',
      render: (_v, record) => (
        <span className={`${codeCls} text-accent`}>{record.waybill}</span>
      ),
    },
    {
      key: 'bookingId',
      title: 'Booking ID',
      render: (_v, record) => (
        <span className={codeCls}>{record.bookingId}</span>
      ),
    },
    {
      key: 'uploadShipmentCode',
      title: 'Batch Code',
      render: (_v, record) =>
        record.uploadShipmentCode ? (
          <span className="font-mono text-[12px] font-semibold text-text-sub-600 whitespace-nowrap">
            {record.uploadShipmentCode}
          </span>
        ) : (
          <span className="text-text-soft-400">—</span>
        ),
    },
    {
      key: 'client',
      title: 'Client',
      render: (_v, record) => {
        const name =
          record.client?.name ??
          providerLookup.get(record.clientId) ??
          `#${record.clientId}`;
        return (
          <div>
            <p className={nameCls}>{name}</p>
            {record.revenueStream && (
              <p className={metaCls}>{record.revenueStream}</p>
            )}
          </div>
        );
      },
    },
    {
      key: 'origin',
      title: 'Origin',
      render: (_v, record) => (
        <div>
          <p className={nameCls}>{record.senderName || '—'}</p>
          <p className={`${metaCls} truncate max-w-[220px]`}>
            {record.originAddress || '—'}
          </p>
        </div>
      ),
    },
    {
      key: 'receiverName',
      title: 'Receiver',
      render: (_v, record) => (
        <div>
          <p className={nameCls}>{record.receiverName}</p>
          <p className={`${metaCls} truncate max-w-[260px]`}>
            {record.destinationAddress}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (_v, record) => <ShipmentStatusBadge status={record.status} />,
    },
    {
      key: 'hub',
      title: 'Hub',
      render: (_v, record) =>
        needsHubResolve(record) ? (
          <span
            className="inline-flex items-center gap-1 text-[12px] text-error-base"
            title="API tidak menemukan hub/pitstop — pilih lewat Set hub"
            data-testid="shipment-hub-unresolved"
          >
            <i className="ri-error-warning-line" /> Hub harus di-resolve manual
          </span>
        ) : record.hub?.name ? (
          <span className={`${nameCls} truncate max-w-[200px] block`}>
            {record.hub.name}
          </span>
        ) : (
          <span className="text-text-soft-400">—</span>
        ),
    },
    {
      key: 'exim',
      title: 'Type',
      // Only the EXIM lanes get a badge; GENERAL / non-4W reads as a muted dash.
      render: (_v, record) =>
        record.exim === 'IMPORT' || record.exim === 'EXPORT' ? (
          <Badge
            status={record.exim === 'IMPORT' ? 'information' : 'feature'}
            size="sm"
          >
            {record.exim === 'IMPORT' ? 'Import' : 'Export'}
          </Badge>
        ) : (
          <span className="text-text-soft-400">—</span>
        ),
    },
    {
      key: 'shipmentType',
      title: 'Shipment Type',
      // Carriage type + its Delivery / Retrieval role (API or import), as two
      // tags.
      render: (_v, record) => (
        <ShipmentTypeTags
          type={record.type}
          anchorRole={displayAnchorRole(record)}
        />
      ),
    },
    {
      key: 'items',
      title: 'Items',
      align: 'right',
      render: (_v, record) => (
        <span className="text-[13px] tabular-nums text-text-sub-600">
          {record.itemCount ?? record.items?.length ?? 0}
        </span>
      ),
    },
    {
      key: 'createdAt',
      title: 'Created',
      render: (_v, record) => (
        <span className={dateCls}>
          {record.createdAt
            ? new Date(record.createdAt).toISOString().slice(0, 10)
            : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      render: (_v, record) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton
              tone="neutral"
              style="ghost"
              size="sm"
              aria-label={`Actions for ${record.waybill}`}
            >
              <i className="ri-more-2-fill text-[18px]" />
            </IconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setDetailShipmentId(record.id)}>
              <i className="ri-eye-line" />
              View details
            </DropdownMenuItem>
            {isShipmentLabelPrintable(record.status) && (
              <DropdownMenuItem
                disabled={labels.preparing}
                onSelect={() => labels.print([record.id])}
              >
                <i className="ri-printer-line" />
                Print label
              </DropdownMenuItem>
            )}
            {record.status === 'IN_DELIVERY' && (
              <DropdownMenuItem
                onSelect={() => openOverride(record, 'COMPLETE')}
              >
                <i className="ri-checkbox-circle-line" />
                Mark completed
              </DropdownMenuItem>
            )}
            {canForceTerminalShipment(record.status) && (
              <DropdownMenuItem onSelect={() => openOverride(record, 'FAIL')}>
                <i className="ri-error-warning-line" />
                Mark failed
              </DropdownMenuItem>
            )}
            {record.status === 'CREATED' && (
              <DropdownMenuItem destructive onSelect={() => openCancel(record)}>
                <i className="ri-close-circle-line" />
                Cancel shipment
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-col gap-3 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-[28px] font-extrabold tracking-tight text-text-strong-950 leading-none">
            Shipments
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-text-sub-600 leading-relaxed">
            Bulk-imported AWBs from client CSV. Preview → commit creates the
            shipments + items that downstream scan & dispatch stages will act
            on.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button tone="neutral" style="stroke" onClick={handleRefresh}>
            <i className="ri-refresh-line mr-1.5 text-[14px]" />
            Refresh
          </Button>
          <Button
            tone="neutral"
            style="stroke"
            onClick={() => labels.print(selectedIds)}
            disabled={labels.preparing}
            data-testid="shipments-print-labels"
          >
            <i
              className={`mr-1.5 text-[14px] ${labels.preparing ? 'ri-loader-4-line animate-spin' : 'ri-printer-line'}`}
            />
            {selectedIds.length > 0
              ? `Print labels (${selectedIds.length})`
              : 'Print labels'}
          </Button>
          <Button
            tone="neutral"
            style="stroke"
            onClick={openAssignHub}
            disabled={hubResolveSelection.length === 0}
            title="Pilih shipment API (CREATED / AT_HUB) untuk set atau ubah hub"
            data-testid="shipments-set-hub"
          >
            <i className="ri-map-pin-2-line mr-1.5 text-[14px]" />
            {hubResolveSelection.length > 0
              ? `Set hub (${hubResolveSelection.length})`
              : 'Set hub'}
          </Button>
          <Button tone="neutral" style="stroke" onClick={openExport}>
            <i className="ri-download-2-line mr-1.5 text-[14px]" />
            Export CSV
          </Button>
          <Button
            tone="neutral"
            style="stroke"
            onClick={() => setImportOpen(true)}
          >
            <i className="ri-upload-2-line mr-1.5 text-[14px]" />
            Import
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <i className="ri-add-line mr-1.5 text-[14px]" />
            Buat Shipment
          </Button>
        </div>
      </div>

      <Card padding="none">
        <div className="flex flex-col gap-3 border-b border-stroke-soft-200 px-4 pt-1 pb-3 lg:flex-row lg:items-end lg:justify-between lg:pb-0">
          <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
            {/* Eleven tabs no longer fit one row on a laptop — wrap like the
                Inbound page instead of overflowing the card. */}
            <TabsList
              variant="line"
              className="h-auto min-h-10 w-auto flex-wrap border-0"
            >
              {TABS.map((t) => {
                // Counts come from `GET /v1/shipments/summary` (v2 keys).
                // Buckets the BE omits stay blank rather than showing a 0 we
                // can't vouch for.
                const count: number | null = !summary
                  ? null
                  : t.key === 'ALL'
                    ? (summary.total ?? null)
                    : (summary[SHIPMENT_SUMMARY_KEYS[t.key]] ?? null);
                return (
                  <TabsTrigger key={t.key} value={t.key}>
                    {t.label}
                    {count !== null && <TabsCount>{count}</TabsCount>}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>

          <div className="flex flex-1 items-center justify-end gap-2 lg:py-2">
            <SearchField
              ref={searchInputRef}
              value={search}
              onChange={setSearch}
              placeholder="Search waybill or booking ID…"
              size="sm"
              className="max-w-xs flex-1"
            />
            <ShipmentFilterPanel
              open={filterOpen}
              onOpenChange={handleFilterOpenChange}
              activeFilterCount={activeFilterCount}
              value={filterDraft}
              onChange={setFilterDraft}
              onApply={applyFilter}
              onReset={resetFilter}
            />
          </div>
        </div>

        <div className="p-4">
          <TableData<Shipment>
            data={items}
            columns={columns}
            loading={tableLoading}
            size="sm"
            selection={{
              selectedKeys: selectedIds,
              onSelectionChange: setSelectedIds,
              getRowKey: (r) => r.id,
              isRowSelectable: (r) => isShipmentLabelPrintable(r.status),
            }}
          />

          {total > 0 && (
            <Paginator
              currentPage={page}
              totalItems={total}
              itemsPerPage={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </div>
      </Card>

      <AssignHubModal
        shipments={assignHubFor}
        hubs={hubs}
        onClose={() => setAssignHubFor([])}
        onAssigned={() => {
          setAssignHubFor([]);
          setSelectedIds([]);
          fetchList();
        }}
      />

      <ImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={handleImported}
        hubs={hubs}
      />

      <CreateShipment4WModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={fetchList}
      />

      <ShipmentDetailPanel
        shipmentId={detailShipmentId || null}
        onClose={() => setDetailShipmentId('')}
        hubs={hubs}
        onShipmentUpdated={(updated) =>
          setItems((prev) =>
            prev.map((s) => (s.id === updated.id ? updated : s))
          )
        }
      />

      <ForceTerminalModal
        shipment={overrideTarget?.shipment ?? null}
        action={overrideTarget?.action ?? 'COMPLETE'}
        open={overrideTarget !== null}
        onClose={() => setOverrideTarget(null)}
        onSwitchAction={(action) =>
          setOverrideTarget((t) => (t ? { ...t, action } : t))
        }
        onDone={(updated) =>
          setItems((prev) =>
            prev.map((s) => (s.id === updated.id ? updated : s))
          )
        }
      />

      <FormModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        icon={<i className="ri-download-2-line" />}
        title="Export shipments"
        subtitle="Choose how to export."
        size="sm"
        closeOnBackdropClick={!exporting}
        closeOnEscape={!exporting}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              tone="neutral"
              style="stroke"
              onClick={() => setExportOpen(false)}
              disabled={exporting}
            >
              Cancel
            </Button>
            <Button
              onClick={submitExport}
              loading={exporting}
              disabled={exporting}
            >
              Export
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          {(
            [
              [
                'default',
                'Default export',
                'Current filters, each client’s configured layout.',
              ],
              [
                'custom',
                'Custom export',
                'Pick a client and a specific template.',
              ],
            ] as const
          ).map(([mode, label, desc]) => (
            <button
              key={mode}
              type="button"
              onClick={() => setExportMode(mode)}
              className={`flex flex-col items-start gap-0.5 rounded-gsm border px-3 py-2.5 text-left transition-colors ${
                exportMode === mode
                  ? 'border-primary bg-(--theme-accent-alpha-10)'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <span className="text-sm font-semibold text-gray-900">
                {label}
              </span>
              <span className="text-xs text-gray-500">{desc}</span>
            </button>
          ))}

          {exportMode === 'custom' && (
            <div className="mt-1 flex flex-col gap-3 border-t border-gray-200 pt-3">
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
                  Client
                </label>
                <SearchSelect
                  options={exportProviders.options.map((p) => ({
                    value: p.id,
                    label: `${p.name} (#${p.id})`,
                  }))}
                  value={exportClientId || undefined}
                  selectedLabelFallback={
                    exportClientId
                      ? exportProviders.labelFor(exportClientId)
                      : undefined
                  }
                  onChange={(value) =>
                    setExportClientId(value ? String(value) : '')
                  }
                  placeholder="Search or select a client…"
                  emptyMessage={exportProviders.error ?? 'No clients found'}
                  loading={exportProviders.loading}
                  hasMore={exportProviders.hasMore}
                  disableLocalFilter
                  onSearchChange={exportProviders.setSearch}
                  onLoadMore={exportProviders.loadMore}
                />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
                  Type
                </label>
                <SearchSelect
                  options={[
                    { value: 'EXPORT', label: 'Export' },
                    { value: 'IMPORT', label: 'Import' },
                    { value: 'GENERAL', label: 'Umum' },
                  ]}
                  value={exportExim || undefined}
                  onChange={(value) =>
                    setExportExim((value as ShipmentExim) ?? '')
                  }
                  placeholder="All types"
                  emptyMessage="No types"
                />
                <p className="mt-1 text-[11px] text-gray-400">
                  Filters the CSV to this shipment type. Leave empty for all
                  types.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
                  Template
                </label>
                <SearchSelect
                  options={exportTemplates.map((t) => ({
                    value: t.key,
                    label: t.key,
                  }))}
                  value={exportTemplateKey || undefined}
                  onChange={(value) =>
                    setExportTemplateKey(String(value ?? ''))
                  }
                  placeholder={
                    exportTemplates.length === 0
                      ? 'Loading…'
                      : 'Search or select a template…'
                  }
                  emptyMessage="No templates"
                  loading={exportTemplates.length === 0}
                />
              </div>
            </div>
          )}
        </div>
      </FormModal>

      {labels.modal}

      <CancelShipmentModal
        shipment={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onCancelled={(updated) => {
          setItems((prev) =>
            prev.map((s) => (s.id === updated.id ? updated : s))
          );
          setCancelTarget(null);
        }}
      />
    </div>
  );
};

export default ShipmentsPage;
